import React, { useState, useMemo } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faTrash, faShieldHalved, faUserSlash, faUserPen, faEye, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationDialog from './dialogs/DeleteConfirmationDialog';
import API from '../config/axiosConfig';
import './UserActionsMenu.scss';

const UserActionsMenu = ({ user, currentUser, department, hospital, fetchUsers }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const submenuOpen = Boolean(submenuAnchorEl);

  const handleMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
  };

  const handleSubmenu = (event) => {
    event.stopPropagation();
    setSubmenuAnchorEl(event.currentTarget);
  };

  const handleDelete = (e) => {
    handleClose(e);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await API.delete(`/api/users/${user._id}`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const roleMap = {
    'אדמין': 'ADMIN',
    'עריכה': 'EDITOR',
    'צפייה': 'USER'
  };

  const handleChangeRole = async (e, role) => {
    handleClose(e);
    try {
      await API.post('/api/users/change-role', {
        userId: user._id,
        departmentId: department._id,
        hospitalId: hospital._id,
        role: role,
      });
      fetchUsers();
    } catch (error) {
      console.error('Failed to change role:', error);
    }
  };

  const handleRemoveFromDepartment = (e) => {
    handleClose(e);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await API.post('/api/users/remove-from-department', {
        email: user.email,
        departmentId: department._id,
        hospitalId: hospital._id,
      });
      fetchUsers();
    } catch (error) {
      console.error('Failed to remove user from department:', error);
    } finally {
      setRemoveDialogOpen(false);
    }
  };

  const availableActions = useMemo(() => {
    const actions = [];
    const canManage = currentUser.isSuperAdmin || (currentUser.roles.some(r => r.role === 'ADMIN' && r.department === department._id));

    if (currentUser.isSuperAdmin) {
      actions.push({ icon: faTrash, text: 'מחק', handler: handleDelete });
      actions.push({ icon: faUserPen, text: 'ערוך', handler: (e) => { handleClose(e); navigate(`/users/${user._id}`); } });
    }

    if (canManage) {
        if (!actions.some(a => a.text === 'ערוך')) {
            actions.push({ icon: faEye, text: 'פרטים', handler: (e) => { handleClose(e); navigate(`/users/${user._id}`); } });
        }
    }
    
    if (canManage) {
        actions.push({ icon: faUserSlash, text: 'הסר מהמחלקה', handler: handleRemoveFromDepartment});
    }

    const roleAction = canManage ? {
        icon: faShieldHalved,
        text: 'הרשאה',
        handler: handleSubmenu,
        submenu: Object.entries(roleMap).map(([text, value]) => ({
            text: text,
            handler: (e) => handleChangeRole(e, value),
            current: user.roles.find(r => {
                // Handle different data structures for department comparison
                return r.department === department._id || 
                       (r.department && typeof r.department === 'object' && r.department._id === department._id) ||
                       String(r.department) === String(department._id);
            })?.role === value
        }))
    } : null;

    if (roleAction) {
        actions.push(roleAction);
    }

    return actions;
  }, [user, currentUser, department, hospital]);

  if (availableActions.length === 0) {
    return null;
  }

  return (
    <div className="user-actions-menu" onClick={(e) => e.stopPropagation()} >
      <IconButton
        className="actions-menu-button"
        aria-label="more"
        onClick={handleMenu}
      >
        <FontAwesomeIcon icon={faGripVertical} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        
      >
        {availableActions.map((action, index) => (
          <MenuItem key={index} onClick={action.handler} style={{color: '#103C6E'}}>
            <ListItemIcon>
              <FontAwesomeIcon icon={action.icon} style={{color: '#103C6E'}} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ noWrap: true }}>{action.text}</ListItemText>
            {action.submenu && <FontAwesomeIcon icon={faChevronLeft} className="submenu-indicator" />}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={submenuAnchorEl}
        open={submenuOpen}
        onClose={handleClose}
      >
        {availableActions.find(a => a.submenu)?.submenu.map((subItem, subIndex) => (
          <MenuItem key={subIndex} onClick={subItem.handler}>
            {subItem.current && <span className="checkmark">✔</span>}
            <ListItemText style={{color: '#103C6E'}}>{subItem.text}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`האם אתה בטוח שברצונך למחוק את המשתמש ${user.name}?`}
      />
      <DeleteConfirmationDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={handleConfirmRemove}
        title={`האם אתה בטוח שברצונך להסיר את ${user.name} מהמחלקה?`}
        confirmText="הסר"
      />
    </div>
  );
};

export default UserActionsMenu;